"use client";
// ===================================================================
// Platform selector — the App the platforms live in (DEC-0362, Rule 32).
//
// This is the surface registered as an App, not the platforms themselves:
// a platform stays an instance (vb_records, kind='platform'), and what Gal
// actually uses every day is this control. It is shell furniture, so its
// app_type is "Shell / Infrastructure", not a content page.
//
// Appears in the sidebar and on the Launchpad, reading and writing the one
// selection in usePlatformFilter so both surfaces move together.
// ===================================================================

import { CircleDashed, Layers } from "lucide-react";
import { ALL_PLATFORMS, UNTAGGED, type PlatformFilter } from "@/lib/platforms/usePlatformFilter";
import type { Language } from "@/contexts/SettingsContext";

interface Props {
  filter: PlatformFilter;
  language: Language;
  /** "compact" for the sidebar rail, "full" for the Launchpad header. */
  variant?: "compact" | "full";
  className?: string;
}

export function PlatformSelector({ filter, language, variant = "compact", className = "" }: Props) {
  const { platforms, selected, select, totals } = filter;

  // Nothing to choose between until the registry arrives. Rendering an
  // "all"-only control would look like the platforms had been lost.
  if (platforms.length === 0) return null;

  const allLabel = language === "he" ? "הכל" : language === "ru" ? "Все" : "All";
  const untaggedLabel =
    language === "he" ? "ללא תיוג" : language === "ru" ? "Без метки" : "Untagged";
  const untaggedHint =
    language === "he"
      ? "דפים שאיש עדיין לא שייך לפלטפורמה"
      : "Pages nobody has assigned to a platform yet";
  const compact = variant === "compact";
  const untaggedActive = selected === UNTAGGED;

  return (
    <div
      data-cc-id="sidebar.platform-selector"
      className={`flex flex-wrap items-center gap-0.5 rounded-lg bg-slate-800/50 p-0.5 ${className}`}
      role="group"
      aria-label={language === "he" ? "בורר פלטפורמה" : "Platform selector"}
    >
      {[{ slug: ALL_PLATFORMS, title: allLabel, appCount: totals.all }, ...platforms].map((p) => {
        const isActive = selected === p.slug;
        // "All" carries the whole inventory, counted DISTINCT in SQL — not the
        // sum of the platform columns, which would count a multi-platform App
        // once per platform and print a total larger than the inventory.
        const count = p.appCount;
        return (
          <button
            key={p.slug}
            type="button"
            onClick={() => select(p.slug)}
            aria-pressed={isActive}
            title={count == null ? p.title : `${p.title} — ${count} App`}
            className={`min-w-0 rounded-md font-medium transition-colors ${
              compact ? "flex-1 px-1 py-1 text-[10px]" : "px-2.5 py-1.5 text-xs"
            } ${
              isActive
                ? "bg-[var(--cc-accent-600-20)] text-[var(--cc-accent-300)]"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <span className="flex min-w-0 items-center justify-center gap-1">
              {p.slug === ALL_PLATFORMS && !compact && <Layers className="h-3 w-3 shrink-0" />}
              <span className="truncate">{p.title}</span>
              {count != null && (
                // shrink-0 so the number survives when the title truncates —
                // a chip reading "המשרד הוו…" with no number would lose the
                // one thing this badge was added for.
                <span className="shrink-0 tabular-nums opacity-60">{count}</span>
              )}
            </span>
          </button>
        );
      })}

      {/* Separated on purpose: this is not a sixth platform, it is the
          residue the other five leave behind — what nobody decided yet. */}
      <button
        type="button"
        onClick={() => select(untaggedActive ? ALL_PLATFORMS : UNTAGGED)}
        aria-pressed={untaggedActive}
        title={
          totals.untagged == null ? untaggedHint : `${untaggedHint} — ${totals.untagged} App`
        }
        className={`rounded-md font-medium transition-colors truncate border-dashed ${
          compact
            ? "basis-full px-1 py-1 text-[10px] border-t border-slate-700/60 mt-0.5 pt-1"
            : "px-2.5 py-1.5 text-xs border rounded-md"
        } ${
          untaggedActive
            ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
            : "text-slate-500 hover:text-slate-300 border-slate-700/60"
        }`}
      >
        <span className="inline-flex min-w-0 items-center gap-1">
          <CircleDashed className="h-3 w-3 shrink-0" />
          <span className="truncate">{untaggedLabel}</span>
          {totals.untagged != null && (
            <span className="shrink-0 tabular-nums opacity-60">{totals.untagged}</span>
          )}
        </span>
      </button>
    </div>
  );
}
